import React, { useEffect, useState } from "react";
import { Check, EyeOff, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { imgSrc } from "@/lib/img";
import { Card, PageTitle, Empty } from "@/admin/ui";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const BADGE = {
  pending: "bg-[#FBF6E8] text-gold",
  approved: "bg-[#E8F0EA] text-forest",
  rejected: "bg-red-50 text-red-700",
};

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("pending");

  const load = () => {
    const q = tab === "all" ? "" : `?status=${tab}`;
    adminApi.get(`/admin/reviews${q}`).then(({ data }) => setReviews(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const setStatus = async (r, status) => {
    try { await adminApi.patch(`/admin/reviews/${r.id}`, { status }); toast.success(`Review ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await adminApi.delete(`/admin/reviews/${r.id}`); toast.success("Review deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-reviews-page">
      <PageTitle title="Ratings & Reviews" sub="Approve customer reviews before they appear on product pages." />
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`reviews-tab-${t.key}`} onClick={() => setTab(t.key)} className={`text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2 rounded-full transition-colors ${tab === t.key ? "bg-forest text-cream" : "bg-white text-forest border border-forest/15 hover:bg-forest/5"}`}>{t.label}</button>
        ))}
      </div>
      <Card className="overflow-hidden">
        {reviews.length === 0 && <Empty text={`No ${tab === "all" ? "" : tab} reviews.`} />}
        <ul className="divide-y divide-forest/10">
          {reviews.map((r) => (
            <li key={r.id} data-testid={`admin-review-row-${r.id}`} className="flex items-start gap-4 px-4 sm:px-6 py-5">
              {r.photo && <img src={imgSrc(r.photo)} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-0.5">{[1,2,3,4,5].map((i)=><Star key={i} size={13} strokeWidth={1.5} className={i<=r.rating?"fill-gold text-gold":"fill-none text-forest/25"} />)}</span>
                  <span className={`text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${BADGE[r.status]}`}>{r.status}</span>
                </div>
                <p className="text-sm font-semibold text-forest mt-1.5">{r.title || r.name}</p>
                <p className="text-xs text-mutedgreen mt-1">{r.body}</p>
                <p className="text-[11px] text-mutedgreen mt-2">— {r.name} · <span className="text-gold font-semibold">{r.product_name}</span></p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {r.status !== "approved" && <button data-testid={`approve-review-${r.id}`} onClick={() => setStatus(r, "approved")} aria-label="Approve" className="p-2 rounded-full text-forest hover:bg-[#E8F0EA]"><Check size={16} /></button>}
                {r.status !== "rejected" && <button data-testid={`reject-review-${r.id}`} onClick={() => setStatus(r, "rejected")} aria-label="Hide" className="p-2 rounded-full text-mutedgreen hover:bg-forest/5"><EyeOff size={16} /></button>}
                <button data-testid={`delete-review-${r.id}`} onClick={() => remove(r)} aria-label="Delete" className="p-2 rounded-full text-mutedgreen hover:text-red-700 hover:bg-red-50"><Trash2 size={16} /></button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
